import React, { FC, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { styled, Theme, Typography } from '@material-ui/core';

import {
  TrajectorySectionCode,
  trajectorySectionCodeLookup,
  TrajectoryViewMode,
  trajectorySectionorWorksheetTypeCodeLookup,
} from '../../../../../config/api/models/Trajectory';
import { useI18nPath } from '../../../../../config/clientRoutes/hooks';
import { Icons, Paragraph } from '../../../../shared/components';
import AddSectionButton from './AddSectionButton';
import { useQueryCache } from 'react-query';
import { UrlParams } from '../../../../shared/types';
import { useAddPageMutation } from 'src/modules/shared/slices/page/api/pages';

type EmptyTrajectoryProps = {
  viewMode: TrajectoryViewMode;
};

const StyledSectionsList = styled('div')<Theme>(({ theme }) => ({
  padding: theme.spacing(1, 0),
}));

const StyledAvailableSectionsWrapper = styled('div')<Theme>(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const EmptyTrajectory: FC<EmptyTrajectoryProps> = ({ viewMode }) => {
  const { t } = useTranslation();
  const { bookId, chapterId, trajectoryId } = useParams<UrlParams>();
  if (!trajectoryId) throw Error('No trajectoryId found!');
  if (!bookId) throw Error('No bookId found!');

  const queryCache = useQueryCache();
  const history = useHistory();
  const { getPath } = useI18nPath();
  const [addedSectionCode, setAddedSectionCode] = useState<TrajectorySectionCode>('0');
  const [addPage, addPageResponse] = useAddPageMutation();

  useEffect(() => {
    if (addPageResponse.isSuccess && !!addedSectionCode) {
      addPageResponse.reset();

      if (!bookId || !chapterId) return;

      history.push(
        getPath('trajectory', {
          bookId: bookId,
          chapterId,
          trajectoryId,
          'sectionCode?': addedSectionCode,
          'sectionName?': t(
            `trajectory.sections.${trajectorySectionCodeLookup[addedSectionCode]}.title`
          ).toLowerCase(),
        })
      );
    }
  }, [
    addPageResponse,
    addedSectionCode,
    bookId,
    chapterId,
    history,
    getPath,
    trajectoryId,
    t,
    queryCache,
  ]);

  const handleClickSection = (sectionCode: TrajectorySectionCode) => {
    if (sectionCode === '0' || sectionCode === '1' || sectionCode === '3') {
      setAddedSectionCode(sectionCode);
      addPage({
        bookId,
        trajectoryId,
        type: trajectorySectionorWorksheetTypeCodeLookup[sectionCode],
      });
    } else {
      history.push(
        getPath('trajectory', {
          bookId: bookId,
          chapterId,
          trajectoryId,
          'sectionCode?': sectionCode,
          'sectionName?': t(
            `trajectory.sections.${trajectorySectionCodeLookup[sectionCode]}.title`
          ).toLowerCase(),
        }),
        {
          addExerciseOrEvaluation: true,
        }
      );
    }
  };

  return (
    <>
      <Paragraph>{t('trajectory.empty.intro')}</Paragraph>
      {viewMode === 'edit' ? (
        <StyledAvailableSectionsWrapper>
          <Typography variant="h3">{t('trajectory.empty.availableSections')}</Typography>
          <StyledSectionsList>
            <AddSectionButton
              icon={Icons.INFO}
              title={t('trajectory.sections.opbouw.title')}
              description={t('trajectory.sections.opbouw.description')}
              onClick={() => handleClickSection('0')}
            />
            <AddSectionButton
              icon={Icons.THEORY}
              title={t('trajectory.sections.theory.title')}
              description={t('trajectory.sections.theory.description')}
              onClick={() => handleClickSection('1')}
            />
            <AddSectionButton
              icon={Icons.EXERCISES}
              title={t('trajectory.sections.exercises.title')}
              description={t('trajectory.sections.exercises.description')}
              onClick={() => handleClickSection('2')}
            />
            <AddSectionButton
              icon={Icons.BOOK}
              title={t('trajectory.sections.study_trainer.title')}
              description={t('trajectory.sections.study_trainer.description')}
              onClick={() => handleClickSection('3')}
            />
          </StyledSectionsList>
        </StyledAvailableSectionsWrapper>
      ) : null}
    </>
  );
};

export default EmptyTrajectory;
